<template>
  <div>
    <div class="form-group">
      <label for="supported-payment-methods">Supported Payment Methods</label>
      <br />
      <small class="text-muted"
        >Controls which of the user's payment methods can be used for payment switching. If not set,
        all saved payment methods will be available. This setting is relevant for customers using
        multiple products (e.g., Deposit and Switch), whose users have bank accounts
        <em>and</em> cards in Atomic's system.</small
      >
      <v-select
        class="mt-3"
        :value="payLink.supportedPaymentMethods"
        :options="PAYMENT_METHOD_OPTIONS"
        :get-option-label="(option) => option.label"
        :get-option-value="(option) => option.value"
        :reduce="(option) => option.value"
        @input="onChangeSupportedPaymentMethods"
        multiple
      />
    </div>
    <div class="form-group">
      <CustomSwitch
        label="Allow users to enter card security details"
        helper-text="Prompts users to enter required card details (e.g., CVV) if missing from their selected payment method."
        :value="payLink.userPatchedCards.enabled"
        @input="onChangeUserPatchedCardsEnabled"
      />
    </div>
    <div class="form-group">
      <CustomSwitch
        label="Allow users to add cards"
        helper-text="Card details are encrypted and stored securely for 24 hours."
        :value="payLink.userProvidedCards.enabled"
        @input="onChangeUserProvidedCardsEnabled"
      />
    </div>
    <div v-if="payLink.userProvidedCards.enabled" class="form-group">
      <label>Approved BIN Codes</label>
      <br />
      <small class="text-muted">
        Only allow users to add cards with the following BIN codes. Enter as comma-separated list of
        6 or 8 digit codes (e.g., "123456, 567890").</small
      >
      <input
        placeholder="123456, 567890"
        class="form-control mt-3"
        type="text"
        :value="payLink.userProvidedCards.binCodes.join(', ')"
        @change="onChangeBinCodes"
        :pattern="binCodesPattern"
      />
    </div>
  </div>
</template>

<script>
import CustomSwitch from '@/components/MainContent/CustomSwitch.vue'
import { merge } from 'lodash-es'
import { BILL_PAYMENT_METHOD_TYPES } from '@/utils/constants'

export default {
  name: 'SettingsFeaturesPayLink',
  components: { CustomSwitch },
  props: {
    data: Object,
  },
  computed: {
    PAYMENT_METHOD_OPTIONS() {
      return [
        {
          label: 'Card',
          value: BILL_PAYMENT_METHOD_TYPES.CARD,
        },
        {
          label: 'Bank',
          value: BILL_PAYMENT_METHOD_TYPES.BANK,
        },
      ]
    },
  },
  data: () => ({
    payLink: {
      supportedPaymentMethods: undefined,
      userPatchedCards: { enabled: false },
      userProvidedCards: { enabled: false, binCodes: [] },
    },
  }),
  methods: {
    onChangeSupportedPaymentMethods(value) {
      this.payLink.supportedPaymentMethods = value
      this.emitChanges()
    },
    onChangeUserProvidedCardsEnabled(value) {
      this.payLink.userProvidedCards.enabled = value
      this.emitChanges()
    },
    onChangeUserPatchedCardsEnabled(value) {
      this.payLink.userPatchedCards.enabled = value
      this.emitChanges()
    },
    onChangeBinCodes(event) {
      if (!event.target.checkValidity()) {
        event.target.reportValidity()
      } else {
        const commaWithOptionalWhiteSpace = /,\s?/
        this.payLink.userProvidedCards.binCodes = event.target.value
          .split(commaWithOptionalWhiteSpace)
          .map((value) => value.trim())
          .filter(Boolean)
        this.emitChanges()
      }
    },
    emitChanges() {
      this.$emit('input', this.payLink)
    },
  },
  created() {
    this.payLink = merge(this.payLink, this.data)

    /**
     * The pattern for a comma separated list of 6 or 8 digit BIN codes.
     *
     * Example: "123456, 567890"
     *
     * The pattern is:
     * - Start with a 6 or 8 digit BIN code
     * - Followed by a comma and optional whitespace
     * - Allow for leading and trailing whitespace since we'll trim it off
     */
    const BIN_CODES_REGEX = /^\s*(\d{6}|\d{8})(,\s*(\d{6}|\d{8}))*\s*$/

    // Need to use `source` to exclude leading and trailing slashes for use
    // in template.
    this.binCodesPattern = BIN_CODES_REGEX.source
  },
}
</script>

<style scoped></style>
